import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './controlPanel';

export type ApiClientDeps = {
  getToken: () => string | null;
  refresh: () => Promise<string | null>;
  onAuthFailure: () => void;
};

const SKIP_AUTH_URL_SUFFIXES = [
  '/api/v2/auth/token/refresh',
  '/api/v2/oidc/redirect',
];

function shouldSkipAuth(url: string): boolean {
  if (SKIP_AUTH_URL_SUFFIXES.some((s) => url.endsWith(s))) return true;
  return /\/api\/v2\/oidc\/callback(\?|$)/.test(url);
}

export function createApiClient({ getToken, refresh, onAuthFailure }: ApiClientDeps) {
  const client = createClient<paths>({ baseUrl: '', credentials: 'include' });

  const auth: Middleware = {
    async onRequest({ request }) {
      if (shouldSkipAuth(request.url)) return request;
      const t = getToken();
      if (t) request.headers.set('Authorization', `Bearer ${t}`);
      return request;
    },
    async onResponse({ request, response }) {
      if (response.status !== 401) return response;
      if (shouldSkipAuth(request.url)) return response;

      const newToken = await refresh();
      if (!newToken) {
        onAuthFailure();
        return response;
      }

      const retry = new Request(request);
      retry.headers.set('Authorization', `Bearer ${newToken}`);
      return fetch(retry);
    },
  };

  client.use(auth);
  return client;
}
